import * as React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { ReactSurveyModel } from "survey-react";

import {
  ApiRequest,
  PersonRequest,
  ProfileData,
  WHATA_ENDPOINTS,
} from "@plinknz/tah-website-elements";

import { CustomerForm, OnFileUploadProps } from "./customer-form";
import { CustomerFormQuestionTypes, PageConfig } from "./customer-form-types";
import {
  AttachmentResponse,
  GrantPayload,
  attachmentEndpoint,
  benefitEndpoint,
  eventEndpoint,
  interactionEndpoint,
  personEndpoint,
} from "../../../config/forms";

import { Loader } from "../../loader";
import { contactApi } from "../../../service/forms/contact/api";
import { whataRequest } from "../../../service/member/whata";
import { UserContext, UserData } from "../../../service/reducers/user";
import { fetchUser } from "../../../utility/load-member";
import { demacronise } from "../../../utility/demacronise";
import { MESSAGE_ENDPOINT } from "../../../config/forms/contact";

interface BenefitFormProps {
  createSurveyConfig: (
    member: PersonRequest,
    user?: UserData
  ) => { pages: PageConfig[] };
  transformData: (
    survey: ReactSurveyModel,
    member: PersonRequest
  ) => GrantPayload;
  questionTypes: CustomerFormQuestionTypes;
  request?: ApiRequest;
  messageRequest?: ApiRequest;
}

export const BenefitForm = ({
  createSurveyConfig,
  transformData,
  questionTypes,
  request = whataRequest,
  messageRequest = contactApi,
}: BenefitFormProps) => {
  const [loading, updateLoading] = React.useState<boolean>(false);
  const [apiLoading, updateApiLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string>(null);
  const [recaptcha, setRecaptcha] = React.useState<string>(null);
  const [payload, setPayload] = React.useState<GrantPayload>(null);
  const {
    state: { user, member },
    dispatch,
  } = React.useContext(UserContext);
  const history = useHistory();
  const location = useLocation();

  async function populateUser() {
    if (!loading) {
      updateLoading(true);
      await fetchUser({
        dispatch,
        history,
        id: user.id,
        location,
      });
      updateLoading(false);
    }
  }

  async function checkLoginStale() {
    // Stale token will result in a non-json (empty) response whivh will
    // cause the get function to throw an error

    try {
      await request.get<ProfileData>(WHATA_ENDPOINTS.me());
    } catch (_) {
      dispatch({ type: "set_member", member: null });
      populateUser();
    }
  }

  React.useEffect(() => {
    if (user?.id) {
      if (member) {
        checkLoginStale();
      } else {
        populateUser();
      }
    }
  }, [user?.id]);

  if (loading) {
    return (
      <div className="splash">
        <Loader size="large" />
      </div>
    );
  }

  if (!member) {
    return <div data-testid="no-member" />;
  }

  const surveyConfig = createSurveyConfig(member, user);

  async function uploadFile({
    file,
    targetId,
  }: {
    file: File;
    targetId: number;
  }) {
    const safeFile = new File([file], demacronise(file.name));
    const formData = new FormData();

    formData.append("file", safeFile);
    const { data } = await request.post(attachmentEndpoint(), formData);
    await request.post(attachmentEndpoint(data as number), {
      members: [targetId],
    });
    const files = ((await request.get(
      personEndpoint(targetId, "attachment")
    )) as unknown) as AttachmentResponse;

    const { url } = files.data.find(({ id }) => id === data);
    return { file, content: url };
  }

  const uploadFiles = async ({ options, targetId }: OnFileUploadProps) => {
    const promises: Promise<{ file: File; content: string }>[] = [];
    for (const file of options.files) {
      promises.push(uploadFile({ file, targetId: targetId || member.id }));
    }

    const uploadedFiles = await Promise.all(promises);
    options.callback("success", uploadedFiles);
  };

  const setData = async (survey: ReactSurveyModel) => {
    const payloadFromForm = transformData(survey, member);
    setPayload(payloadFromForm);
  };

  const sendDataToServer = async () => {
    updateApiLoading(true);

    try {
      if (payload.data.benefit) {
        await request.post(
          benefitEndpoint(payload.targetId),
          payload.data.benefit
        );
      } else if (payload.data.event) {
        await request.post(eventEndpoint(payload.targetId), payload.data.event);
      }

      await request.post(
        interactionEndpoint(payload.targetId),
        payload.data.interaction
      );
      const data = {
        data: payload.data.email,
        recaptcha,
        tenant: process.env.TAH_TENANT,
      };

      if (payload.data.email) {
        await messageRequest.post(MESSAGE_ENDPOINT, data);
      }
    } catch (sendError) {
      console.error(sendError);

      if (sendError instanceof Error) {
        setError(sendError.message);
      }
    } finally {
      updateApiLoading(false);
    }
  };

  React.useEffect(() => {
    if (payload && recaptcha) {
      sendDataToServer();
    }
  }, [payload, recaptcha]);

  return (
    <CustomerForm
      onComplete={setData}
      apiRequestInProgress={apiLoading}
      apiError={error}
      surveyConfig={surveyConfig}
      questionTypes={questionTypes}
      onFileUpload={uploadFiles}
      setRecaptcha={setRecaptcha}
    />
  );
};
